import { useEffect } from "react";
import { useSelector } from "react-redux";

import urls from "@/constants/Urls";
import { getVersion, saveSetting } from "@/utils/utility";
import Valkyrie, {
  viArrowRight,
  viBook,
  viBoxOpenFull,
  viEarth,
  viAt,
  viSparkles,
  viChain,
} from "@sippy-platform/valkyrie";

import { Box, Button, Card, Divider, Stack, Typography } from "@mui/material";

import FeatureBlock from "./about/FeatureBlock";

export default function About() {
  const settings = useSelector((state) => state.auth.settings);

  useEffect(() => {
    if (settings.version !== getVersion("major")) {
      saveSetting("version", getVersion("major"));
    }
  }, [settings.version]);

  return (
    <Stack
      sx={{
        gap: 3,
      }}
    >
      <Box
        sx={{
          borderRadius: 1,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "flex-end",
          p: 3,
          height: 200,
        }}
        className="bg-linear-to-br from-violet-300 from-16% via-indigo-400 via-50% to-blue-500 dark:from-violet-500 dark:via-indigo-600 dark:to-blue-800"
      >
        <Typography variant="h1" sx={{ color: "white" }}>
          My Sippy {getVersion("readable")}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: "1.2rem" }}>
        Sippy 3.19 introduces AI analysis of your calls so you can quickly see a summary, transcript, and the sentiment
        of your calls right from the call log.
      </Typography>
      {/* <Box
        sx={{
          containerType: 'inline-size',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(16rem, 100%), 1fr))',
          gap: 3
        }}
      >
        <FeatureHighlight
          feature="queues"
          title="Queue insights."
          description="Take a closer look at what's happening in your queues with the brand new queue statistics and queue logs."
        />
      </Box>
      <Divider sx={{ borderBottomWidth: 3 }} /> */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(min(16rem, 100%), 1fr))",
          gap: 3,
        }}
      >
        <FeatureBlock
          icon={viSparkles}
          title="AI call analysis"
          description="When you enable AI features, your recorded calls will now include a summary, transcription, and sentiment information about your call."
        />
        <FeatureBlock
          icon={viAt}
          title="Mail addresses for users"
          description="You can now add an email address to your users and mobile users."
        />
        <FeatureBlock
          icon={viChain}
          title="Deeplinking in logs and statistics"
          description="It is now possible to link directly to an event in the webhook log and the new call details in the call log, as well as to the details of an agent in the queue statistics."
        />
      </Box>
      <Divider sx={{ borderBottomWidth: 3 }} />
      <Card sx={{ p: 3, containerType: "inline-size" }}>
        <Stack
          direction="column"
          sx={{
            alignItems: "flex-end",
            gap: 2,

            ["@container (min-width: 30rem)"]: {
              flexDirection: "row",
              alignItems: "center",
              gap: 5,
            },
          }}
        >
          <Stack
            sx={{
              gap: 1,
            }}
          >
            <Typography variant="h4" sx={{ m: 0 }}>
              And so much more
            </Typography>
            <Typography>
              There is still so much more to talk about. For a more comprehensive list of changes in My Sippy{" "}
              {getVersion("major")}, read the release notes in the documentation.
            </Typography>
          </Stack>
          <Button
            variant="contained"
            size="large"
            href={`${urls.docs}updates/my-sippy-${getVersion("major").replace(".", "-")}`}
            target="_blank"
            startIcon={<Valkyrie icon={viBoxOpenFull} />}
            endIcon={<Valkyrie icon={viArrowRight} />}
            sx={{ px: 3, py: 1.5 }}
          >
            Release notes
          </Button>
        </Stack>
      </Card>
      <Stack
        direction="row"
        sx={{
          gap: 0.5,
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box
          component="img"
          src={`/${import.meta.env.SIPPY_ICON}`}
          style={{ height: "32px", width: "auto", display: "block" }}
          alt="Sippy, your way of communicating"
        />
        <Stack
          direction="row"
          sx={{
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <Button size="small" color="inherit" href={urls.docs} target="_blank" startIcon={<Valkyrie icon={viBook} />}>
            Documentation
          </Button>
          <Button
            size="small"
            color="inherit"
            href={`https://sippy.cloud`}
            target="_blank"
            startIcon={<Valkyrie icon={viEarth} />}
          >
            Sippy.cloud
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
